import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { webpages_Role } from 'app/shared/models/domain/data.model';

@Injectable({
    providedIn: 'root'
})
export class AdminAuthGuard  {
    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _router: Router
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     *
     * @param route
     * @param state
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this._check(redirectUrl);
    }

    /**
     * Can activate child
     *
     * @param childRoute
     * @param state
     */
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this._check(redirectUrl);
    }

    /**
     * Can load
     *
     * @param route
     * @param segments
     */
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
        return this._check('/');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check the authenticated status
     *
     * @param redirectURL
     * @private
     */
    private _check(redirectURL: string): Observable<boolean> {
        // Check the authentication status
        return this._authService.check()
            .pipe(
                switchMap((authenticated) => {
                    // If the user is not authenticated...
                    if (!authenticated) {
                        this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
                        return of(false);
                    }

                    // Wait for roles to be loaded if empty
                    const roles = this._authService.localUserRoles;
                    if ((!roles || roles.length === 0) && this._authService.accessToken) {
                        // Try to fetch profile and roles again
                        return this._authService.profileUsingToken().pipe(
                            switchMap((profileResponse: any) => {
                                const loadedRoles = this._authService.localUserRoles;
                                let isAdmin = false;
                                loadedRoles.forEach((role: webpages_Role) => {
                                    if (environment.roleTabs['admin'].includes(role.roleName)) {
                                        isAdmin = true;
                                    }
                                });
                                if (!isAdmin) {
                                    this._router.navigate(['error/403']);
                                    return of(false);
                                }
                                return of(true);
                            })
                        );
                    } else {
                        let isAdmin = false;
                        roles.forEach((role: webpages_Role) => {
                            if (environment.roleTabs['admin'].includes(role.roleName)) {
                                isAdmin = true;
                            }
                        });
                        if (!isAdmin) {
                            this._router.navigate(['error/403']);
                            return of(false);
                        }
                        return of(true);
                    }
                })
            );
    }
}
